import type {
  VisualEditorConfig,
  VisualEditorPage,
  VisualEditorWidget,
  MediaUploadResult,
} from '../types.ts';

export class VisualEditorApiService {
  private config: VisualEditorConfig | null = null;
  private tenantBase: string = '';

  constructor() {
    this.detectInitialTenantBase();
  }

  private detectInitialTenantBase(): void {
    if (typeof document === 'undefined') return;

    // 1. Detect from data-tenant-prefix on <body> or <html>
    const bodyTenantPrefix = document.body?.dataset?.tenantPrefix;
    if (bodyTenantPrefix) {
      this.tenantBase = '/' + bodyTenantPrefix.replace(/^\/+|\/+$/g, '');
      return;
    }

    // 2. Detect from script tag: e.g. src="/bluefreightintl/Loumars.VisualEditor/js/visual-editor.js"
    const script = document.querySelector<HTMLScriptElement>('script[src*="Loumars.VisualEditor"]');
    if (script) {
      const srcAttr = script.getAttribute('src') || '';
      const idx = srcAttr.indexOf('/Loumars.VisualEditor');
      if (idx > 0) {
        this.tenantBase = srcAttr.substring(0, idx);
        return;
      } else if (idx === 0) {
        this.tenantBase = '';
        return;
      }
      try {
        const parsed = new URL(script.src, window.location.origin);
        const urlIdx = parsed.pathname.indexOf('/Loumars.VisualEditor');
        if (urlIdx > 0) {
          this.tenantBase = parsed.pathname.substring(0, urlIdx);
          return;
        } else if (urlIdx === 0) {
          this.tenantBase = '';
          return;
        }
      } catch {
        // Fallback below
      }
    }

    // Default to empty tenant base (root)
    this.tenantBase = '';
  }

  public setConfig(config: VisualEditorConfig): void {
    this.config = config;
    if (config.tenantPrefix) {
      this.tenantBase = '/' + config.tenantPrefix.replace(/^\/+|\/+$/g, '');
    } else {
      this.tenantBase = '';
    }
  }

  public getTenantBase(): string {
    return this.tenantBase;
  }

  private getUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : '/' + endpoint;
    return this.tenantBase + cleanEndpoint;
  }

  private getHeaders(isFormData: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {};
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.config?.antiforgeryToken) {
      headers['RequestVerificationToken'] = this.config.antiforgeryToken;
      headers['X-CSRF-TOKEN'] = this.config.antiforgeryToken;
      headers['X-XSRF-TOKEN'] = this.config.antiforgeryToken;
    }

    return headers;
  }

  public async fetchConfig(): Promise<VisualEditorConfig> {
    const res = await fetch(this.getUrl('/api/visual-editor/config'), {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      throw new Error(`Failed to load visual editor config (${res.status})`);
    }

    const data = (await res.json()) as VisualEditorConfig;
    this.setConfig(data);
    return data;
  }

  public async getPage(path?: string, contentItemId?: string): Promise<VisualEditorPage> {
    const params = new URLSearchParams();
    if (path) params.set('path', path);
    if (contentItemId) params.set('contentItemId', contentItemId);

    const res = await fetch(this.getUrl(`/api/visual-editor/page?${params.toString()}`), {
      method: 'GET',
      credentials: 'same-origin',
      headers: this.getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to load page content (${res.status})`);
    }

    return (await res.json()) as VisualEditorPage;
  }

  public async getWidget(
    pageContentItemId: string,
    widgetContentItemId: string
  ): Promise<VisualEditorWidget> {
    const res = await fetch(
      this.getUrl(
        `/api/visual-editor/widget/${encodeURIComponent(widgetContentItemId)}?pageContentItemId=${encodeURIComponent(pageContentItemId)}`
      ),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: this.getHeaders(),
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to load widget (${res.status})`);
    }

    return (await res.json()) as VisualEditorWidget;
  }

  public async updateWidget(
    pageContentItemId: string,
    widgetContentItemId: string,
    data: Record<string, unknown>
  ): Promise<boolean> {
    const res = await fetch(this.getUrl('/api/visual-editor/widget/update'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({ pageContentItemId, widgetContentItemId, data }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Failed to update widget');
    }

    return true;
  }

  public async reorderWidgets(
    pageContentItemId: string,
    widgetIds: string[],
    parentWidgetContentItemId?: string | null
  ): Promise<boolean> {
    const res = await fetch(this.getUrl('/api/visual-editor/page/reorder'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({ pageContentItemId, widgetIds, parentWidgetContentItemId }),
    });

    if (!res.ok) {
      throw new Error('Failed to reorder widgets');
    }

    return true;
  }

  public async addWidget(
    pageContentItemId: string,
    widgetType: string,
    insertIndex?: number,
    parentWidgetContentItemId?: string | null,
    data?: Record<string, unknown>
  ): Promise<VisualEditorWidget> {
    const res = await fetch(this.getUrl('/api/visual-editor/page/add-widget'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({
        pageContentItemId,
        widgetType,
        insertIndex,
        parentWidgetContentItemId,
        data,
      }),
    });

    if (!res.ok) {
      throw new Error('Failed to add widget');
    }

    return (await res.json()) as VisualEditorWidget;
  }

  public async deleteWidget(
    pageContentItemId: string,
    widgetContentItemId: string,
    parentWidgetContentItemId?: string | null
  ): Promise<boolean> {
    const params = new URLSearchParams({ pageContentItemId });
    if (parentWidgetContentItemId) {
      params.set('parentWidgetContentItemId', parentWidgetContentItemId);
    }

    const res = await fetch(
      this.getUrl(
        `/api/visual-editor/page/widget/${encodeURIComponent(widgetContentItemId)}?${params.toString()}`
      ),
      {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: this.getHeaders(),
      }
    );

    if (!res.ok) {
      throw new Error('Failed to delete widget');
    }

    return true;
  }

  public async updatePageSettings(
    pageContentItemId: string,
    displayText: string,
    pageTitle: string,
    metaDescription: string,
    path: string
  ): Promise<boolean> {
    const res = await fetch(this.getUrl('/api/visual-editor/page/settings'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({ pageContentItemId, displayText, pageTitle, metaDescription, path }),
    });

    if (!res.ok) {
      throw new Error('Failed to update page settings');
    }

    return true;
  }

  public async publishPage(pageContentItemId: string): Promise<boolean> {
    const res = await fetch(this.getUrl('/api/visual-editor/page/publish'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({ pageContentItemId }),
    });

    if (!res.ok) {
      throw new Error('Failed to publish page');
    }

    return true;
  }

  public async saveDraft(pageContentItemId: string): Promise<boolean> {
    const res = await fetch(this.getUrl('/api/visual-editor/page/save-draft'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(),
      body: JSON.stringify({ pageContentItemId }),
    });

    if (!res.ok) {
      throw new Error('Failed to save page draft');
    }

    return true;
  }

  public async uploadMedia(
    file: File,
    folder: string = 'visual-editor'
  ): Promise<MediaUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const res = await fetch(this.getUrl('/api/visual-editor/media/upload'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.getHeaders(true),
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Failed to upload media file');
    }

    return (await res.json()) as MediaUploadResult;
  }

  public async deleteMedia(url: string): Promise<boolean> {
    const params = new URLSearchParams({ url });
    const res = await fetch(this.getUrl(`/api/visual-editor/media?${params.toString()}`), {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: this.getHeaders(),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Failed to delete media file');
    }

    return true;
  }
}

export const visualEditorApi = new VisualEditorApiService();
